import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { Modal } from '../components/Modal';
import { notify } from '../utils/notify';
import { FileAttachment } from '../components/FileAttachment';

interface Event {
  id: number;
  title: string;
  description: string;
  datetime: string;
  price: number;
  max_participants: number;
  available_slots: number;
  location: string;
  is_active: boolean;
  bookings_count: number;
  waitlist_count?: number;
}

interface Booking {
  booking_id: number;
  booked_at: string;
  status: string;
  user_id: number;
  messenger_id: string;
  chat_id?: string;
  user_name: string;
  username: string;
  is_guest: boolean;
  guest_name?: string;
  admin_note?: string;
}

interface User {
  id: number;
  full_name: string;
  username?: string;
  messenger_id: string;
  role: string;
  admin_note?: string;
  created_at: string;
}

interface Guest {
  id: number;
  full_name: string;
  created_at: string;
  bookings_count: number;
  user_name: string;
  messenger_id?: string;
}

interface WaitlistEntry {
  waitlist_id: number;
  created_at: string;
  status: string;
  user_id: number;
  messenger_id: string;
  chat_id?: string;
  user_name: string;
  username: string;
  is_guest: boolean;
  guest_name?: string;
  guest_id?: number;
  parent_waitlist_id?: number;
  parent_user_name?: string;
  admin_note?: string;
}

interface UserDetails {
  id: number;
  full_name: string;
  username?: string;
  messenger_id: string;
  chat_id?: string;
  role: string;
  admin_note?: string;
  created_at: string;
  bookings_count: number;
  guests_count: number;
  active_bookings: Array<{
    booking_id: number;
    is_guest: boolean;
    guest_name?: string;
    status: string;
    event_id: number;
    event_title: string;
    event_datetime: string;
  }>;
}

// ============================================================
//  🕐 ВСПОМОГАТЕЛЬНАЯ ФУНКЦИЯ ДЛЯ ПРЕОБРАЗОВАНИЯ UTC → ЛОКАЛЬНОЕ ВРЕМЯ
// ============================================================

function toLocalDatetimeString(isoString: string): string {
  const d = new Date(isoString);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [selectedEventBookings, setSelectedEventBookings] = useState<Booking[] | null>(null);
  const [selectedEventTitle, setSelectedEventTitle] = useState<string>('');
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; eventId: number | null }>({
    isOpen: false,
    eventId: null,
  });
  const [users, setUsers] = useState<User[]>([]);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [attachment, setAttachment] = useState<{ type: string; token: string } | null>(null);
  const [showGuestsModal, setShowGuestsModal] = useState(false);
  const [guests, setGuests] = useState<Guest[]>([]);

  const [selectedEventWaitlist, setSelectedEventWaitlist] = useState<WaitlistEntry[] | null>(null);
  const [selectedWaitlistEventTitle, setSelectedWaitlistEventTitle] = useState<string>('');
  const [selectedWaitlistEventId, setSelectedWaitlistEventId] = useState<number | null>(null);
  const [showUserCard, setShowUserCard] = useState<number | null>(null);
  const [showBookClientModal, setShowBookClientModal] = useState<Event | null>(null);

  // Текущий пользователь админки (для проверки прав на смену ролей)
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);

  const formRef = useRef<HTMLDivElement>(null);

  const isWebAdmin = !!localStorage.getItem('adminToken');

  const handleLogout = () => {
    if (window.confirm('Вы уверены, что хотите выйти?')) {
      localStorage.removeItem('adminToken');
      notify.success('Вы вышли из системы');
      navigate('/admin/login');
    }
  };

  // ============================================================
  //  ЗАГРУЗКА ТЕКУЩЕГО ПОЛЬЗОВАТЕЛЯ (роль)
  // ============================================================
  const loadCurrentUser = async () => {
    try {
      const response = await api.get('/me');
      setCurrentUserRole(response.data.role || null);
    } catch (error) {
      console.error('❌ Ошибка загрузки текущего пользователя:', error);
    }
  };

  const loadEvents = async (archived = false) => {
    setLoading(true);
    try {
      const url = archived ? '/admin/events/archived' : '/admin/events';
      const response = await api.get(url);
      setEvents(response.data);
    } catch (error: any) {
      console.error('❌ Ошибка загрузки мероприятий:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        notify.error('Сессия истекла. Войдите заново.');
        navigate('/admin/login');
        return;
      }
      notify.error('Не удалось загрузить мероприятия');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error: any) {
      console.error('❌ Ошибка загрузки пользователей:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        notify.error('Сессия истекла. Войдите заново.');
        navigate('/admin/login');
        return;
      }
      notify.error('Не удалось загрузить пользователей');
    }
  };

  const loadGuests = async () => {
    try {
      const response = await api.get('/admin/guests');
      setGuests(response.data);
    } catch (error: any) {
      console.error('❌ Ошибка загрузки гостей:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        notify.error('Сессия истекла. Войдите заново.');
        navigate('/admin/login');
        return;
      }
      notify.error('Не удалось загрузить гостей');
    }
  };

  useEffect(() => {
    loadEvents(false);
    loadCurrentUser();
  }, []);

  const handleToggleArchive = () => {
    const newState = !showArchived;
    setShowArchived(newState);
    loadEvents(newState);
  };

  const openForm = (event?: Event) => {
    setEditingEvent(event || null);
    setShowForm(true);
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const firstInput = formRef.current.querySelector('input');
        if (firstInput) {
          firstInput.focus();
        }
      }
    }, 100);
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/admin/events/${id}`);
      await loadEvents(showArchived);
      notify.success('Мероприятие удалено');
    } catch (error: any) {
      console.error('❌ Ошибка удаления:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        notify.error('Сессия истекла. Войдите заново.');
        navigate('/admin/login');
        return;
      }
      notify.error('Не удалось удалить мероприятие');
    }
    setDeleteModal({ isOpen: false, eventId: null });
  };

  const handleDeleteWaitlistEntry = async (waitlistId: number, userName: string) => {
    if (!window.confirm(`Вы уверены, что хотите удалить запись пользователя ${userName} из листа ожидания?`)) return;

    try {
      await api.delete(`/admin/waitlist/${waitlistId}`);
      notify.success('Запись удалена из листа ожидания');

      if (selectedWaitlistEventId) {
        const response = await api.get(`/admin/events/${selectedWaitlistEventId}/waitlist`);
        setSelectedEventWaitlist(response.data);
      }
    } catch (error) {
      console.error('❌ Ошибка удаления из листа ожидания:', error);
      notify.error('Не удалось удалить запись');
    }
  };

  const handleRestore = async (id: number) => {
    if (!window.confirm('Восстановить мероприятие из архива?')) return;
    try {
      await api.put(`/admin/events/${id}/restore`);
      notify.success('Мероприятие восстановлено');
      loadEvents(showArchived);
    } catch (error: any) {
      console.error('❌ Ошибка восстановления:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        notify.error('Сессия истекла. Войдите заново.');
        navigate('/admin/login');
        return;
      }
      notify.error('Не удалось восстановить мероприятие');
    }
  };

  const handleSave = async (data: any) => {
    try {
      const sendData = {
        ...data,
        datetime: new Date(data.datetime).toISOString(),
      };

      if (editingEvent) {
        await api.put(`/admin/events/${editingEvent.id}`, sendData);
        notify.success('Мероприятие обновлено');
      } else {
        await api.post('/admin/events', sendData);
        notify.success('Мероприятие создано');
      }
      setShowForm(false);
      setEditingEvent(null);
      await loadEvents(showArchived);
    } catch (error: any) {
      console.error('❌ Ошибка сохранения:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        notify.error('Сессия истекла. Войдите заново.');
        navigate('/admin/login');
        return;
      }
      notify.error('Не удалось сохранить мероприятие');
    }
  };

  const handleViewBookings = async (eventId: number, eventTitle: string) => {
    try {
      const response = await api.get(`/admin/events/${eventId}/bookings`);
      setSelectedEventBookings(response.data);
      setSelectedEventTitle(eventTitle);
      setSelectedEventId(eventId);
    } catch (error: any) {
      console.error('❌ Ошибка загрузки записей:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        notify.error('Сессия истекла. Войдите заново.');
        navigate('/admin/login');
        return;
      }
      notify.error('Не удалось загрузить записи');
    }
  };

  const handleViewWaitlist = async (eventId: number, eventTitle: string) => {
    try {
      const response = await api.get(`/admin/events/${eventId}/waitlist`);
      setSelectedEventWaitlist(response.data);
      setSelectedWaitlistEventTitle(eventTitle);
      setSelectedWaitlistEventId(eventId);
    } catch (error: any) {
      console.error('❌ Ошибка загрузки листа ожидания:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        notify.error('Сессия истекла. Войдите заново.');
        navigate('/admin/login');
        return;
      }
      notify.error('Не удалось загрузить лист ожидания');
    }
  };

  const handleDeleteBooking = async (bookingId: number, userName: string) => {
    if (!window.confirm(`Вы уверены, что хотите удалить запись пользователя ${userName}?`)) return;

    try {
      await api.delete(`/admin/bookings/${bookingId}`);
      notify.success('Запись удалена');

      if (selectedEventId) {
        const response = await api.get(`/admin/events/${selectedEventId}/bookings`);
        setSelectedEventBookings(response.data);
      }
    } catch (error: any) {
      console.error('❌ Ошибка удаления записи:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        notify.error('Сессия истекла. Войдите заново.');
        navigate('/admin/login');
        return;
      }
      notify.error('Не удалось удалить запись');
    }
  };

  const handleSendMessageToUser = async (
    chatId: string,
    userName: string,
    fileAttachment?: { type: string; token: string } | null
  ) => {
    const message = prompt(`Введите сообщение для пользователя ${userName}:`);
    if (message && message.trim()) {
      try {
        const attachments = fileAttachment
          ? [{ type: fileAttachment.type, token: fileAttachment.token }]
          : [];

        await api.post('/admin/broadcast', {
          recipient: [parseInt(chatId)],
          message: message.trim(),
          attachments,
          includeOwnerLink: true,
        });
        notify.success(`✅ Сообщение отправлено пользователю ${userName}`);
      } catch (error: any) {
        console.error('❌ Ошибка отправки сообщения:', error);
        if (error.response?.status === 401) {
          localStorage.removeItem('adminToken');
          notify.error('Сессия истекла. Войдите заново.');
          navigate('/admin/login');
          return;
        }
        notify.error('Не удалось отправить сообщение');
      }
    }
  };

  const handleSendMessageToAllBookings = async () => {
    if (!selectedEventBookings || selectedEventBookings.length === 0) {
      notify.error('Нет записей на это мероприятие');
      return;
    }

    const message = prompt('Введите сообщение для всех записавшихся:');
    if (message && message.trim()) {
      const chatIds = selectedEventBookings
        .filter((b): b is Booking & { chat_id: string } => !!b.chat_id)
        .map(b => parseInt(b.chat_id));

      if (chatIds.length === 0) {
        notify.error('Нет пользователей с chat_id для отправки');
        return;
      }

      try {
        const attachments = attachment ? [{ type: attachment.type, token: attachment.token }] : [];
        await api.post('/admin/broadcast', {
          recipient: chatIds,
          message: message.trim(),
          attachments,
        });
        notify.success(`✅ Сообщение отправлено ${chatIds.length} записавшимся`);
      } catch (error: any) {
        console.error('❌ Ошибка отправки сообщения:', error);
        if (error.response?.status === 401) {
          localStorage.removeItem('adminToken');
          notify.error('Сессия истекла. Войдите заново.');
          navigate('/admin/login');
          return;
        }
        notify.error('Не удалось отправить сообщение');
      }
    }
  };

  const handleUsersButtonClick = async () => {
    await loadUsers();
    setShowUsersModal(true);
  };

  const handleGuestsButtonClick = async () => {
    await loadGuests();
    setShowGuestsModal(true);
  };

  const openBroadcastModal = async () => {
    await loadUsers();
    setShowBroadcastModal(true);
    setAttachment(null);
  };

  const handleUpdateUserNote = async (userId: number, note: string) => {
    try {
      await api.put(`/admin/users/${userId}/note`, { note });
      notify.success('Заметка сохранена');
      setUsers(prev =>
        prev.map(u =>
          u.id === userId ? { ...u, admin_note: note || undefined } : u
        )
      );
    } catch (error) {
      console.error('❌ Ошибка сохранения заметки:', error);
      notify.error('Не удалось сохранить заметку');
    }
  };

  // ============================================================
  //  👑 СМЕНА РОЛИ ПОЛЬЗОВАТЕЛЯ
  // ============================================================
  const handleUpdateUserRole = async (userId: number, newRole: string) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      notify.success('Роль изменена');
      setUsers(prev =>
        prev.map(u =>
          u.id === userId ? { ...u, role: newRole } : u
        )
      );
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Не удалось изменить роль';
      notify.error(msg);
      throw error; // пробрасываем наверх, чтобы UserCardModal мог откатить состояние
    }
  };

  if (loading) return <div style={{ padding: 20 }}>Загрузка...</div>;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>📋 Админ-панель</h1>
        <div>
          {isWebAdmin && (
            <span style={{ fontSize: 14, color: '#666', marginRight: 16 }}>
              🌐 Веб-админ
            </span>
          )}
          <button
            onClick={handleLogout}
            style={{
              padding: '6px 16px',
              cursor: 'pointer',
              backgroundColor: '#ff4444',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              fontSize: 14,
            }}
          >
            🚪 Выйти
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <button onClick={() => openForm()} style={{ padding: '8px 16px', cursor: 'pointer' }}>
          ➕ Создать мероприятие
        </button>
        <button onClick={handleToggleArchive} style={{ padding: '8px 16px', cursor: 'pointer', backgroundColor: showArchived ? '#4CAF50' : '#f0f0f0' }}>
          {showArchived ? '📂 Активные' : '📂 Архив'}
        </button>
        <button onClick={handleUsersButtonClick} style={{ padding: '8px 16px', cursor: 'pointer' }}>
          👥 Пользователи
        </button>
        <button onClick={handleGuestsButtonClick} style={{ padding: '8px 16px', cursor: 'pointer' }}>
          👤 Гости
        </button>
        <button onClick={openBroadcastModal} style={{ padding: '8px 16px', cursor: 'pointer' }}>
          ✉️ Рассылка
        </button>
      </div>

      {showForm && (
        <div ref={formRef}>
          <EventForm
            event={editingEvent}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditingEvent(null); }}
          />
        </div>
      )}

      {selectedEventBookings && (
        <BookingsModal
          eventTitle={selectedEventTitle}
          bookings={selectedEventBookings}
          onClose={() => {
            setSelectedEventBookings(null);
            setSelectedEventId(null);
          }}
          onDeleteBooking={handleDeleteBooking}
          onSendMessageToUser={handleSendMessageToUser}
          onSendMessageToAll={handleSendMessageToAllBookings}
          onViewUser={(userId) => setShowUserCard(userId)}
          attachment={attachment}
          setAttachment={setAttachment}
        />
      )}

      {selectedEventWaitlist && (
        <WaitlistModal
          eventTitle={selectedWaitlistEventTitle}
          entries={selectedEventWaitlist}
          onClose={() => {
            setSelectedEventWaitlist(null);
            setSelectedWaitlistEventId(null);
          }}
          onDeleteEntry={handleDeleteWaitlistEntry}
          onSendMessageToUser={handleSendMessageToUser}
          onViewUser={(userId) => setShowUserCard(userId)}
          attachment={attachment}
          setAttachment={setAttachment}
        />
      )}

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, eventId: null })}
        onConfirm={() => deleteModal.eventId && handleDelete(deleteModal.eventId)}
        title="Удаление мероприятия"
        message="Вы уверены, что хотите удалить это мероприятие? Все записи будут удалены, а пользователи получат уведомление."
        confirmText="Удалить"
      />

      {events.map(event => (
        <div key={event.id} style={{
          border: '1px solid #ddd',
          borderRadius: 8,
          padding: 16,
          marginBottom: 12,
          backgroundColor: '#fff'
        }}>
          <h3 style={{ margin: 0 }}>{event.title}</h3>
          <p style={{ margin: '4px 0', color: '#666' }}>
            📅 {new Date(event.datetime).toLocaleString()}
          </p>
          <p style={{ margin: '4px 0' }}>
            👥 {event.bookings_count || 0} записей | 🆓 {event.available_slots} / {event.max_participants}
            {event.waitlist_count !== undefined && event.waitlist_count > 0 && (
              <span style={{ marginLeft: 8, color: '#ff9800' }}>⏳ {event.waitlist_count} в листе ожидания</span>
            )}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button onClick={() => openForm(event)} style={{ padding: '4px 12px', cursor: 'pointer' }}>
              ✏️ Редактировать
            </button>
            <button onClick={() => setDeleteModal({ isOpen: true, eventId: event.id })} style={{ padding: '4px 12px', cursor: 'pointer', color: 'red' }}>
              🗑️ Удалить
            </button>
            <button onClick={() => handleViewBookings(event.id, event.title)} style={{ padding: '4px 12px', cursor: 'pointer' }}>
              📋 Записи ({event.bookings_count || 0})
            </button>
            <button onClick={() => handleViewWaitlist(event.id, event.title)} style={{ padding: '4px 12px', cursor: 'pointer' }}>
              ⏳ Лист ожидания ({event.waitlist_count || 0})
            </button>
            <button
              onClick={() => setShowBookClientModal(event)}
              style={{
                padding: '4px 12px',
                cursor: 'pointer',
                backgroundColor: '#007AFF',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
              }}
            >
              ➕ Записать клиента
            </button>
            {showArchived && (
              <button onClick={() => handleRestore(event.id)} style={{ padding: '4px 12px', cursor: 'pointer', color: '#2196F3' }}>
                ↩️ Восстановить
              </button>
            )}
          </div>
        </div>
      ))}

      {showUserCard !== null && (
        <UserCardModal
          userId={showUserCard}
          currentUserRole={currentUserRole}
          onClose={() => setShowUserCard(null)}
          onNoteUpdate={(userId, note) => {
            setUsers(prev =>
              prev.map(u =>
                u.id === userId ? { ...u, admin_note: note || undefined } : u
              )
            );
          }}
          onRoleUpdate={handleUpdateUserRole}
          onSendMessage={(chatId, userName) => handleSendMessageToUser(chatId, userName, attachment)}
        />
      )}

      <UsersModal
        isOpen={showUsersModal}
        users={users}
        onClose={() => setShowUsersModal(false)}
        onUpdateNote={handleUpdateUserNote}
        onViewUser={(userId) => setShowUserCard(userId)}
      />
      <GuestsModal isOpen={showGuestsModal} guests={guests} onClose={() => setShowGuestsModal(false)} />
      <BroadcastModal
        isOpen={showBroadcastModal}
        users={users}
        onClose={() => setShowBroadcastModal(false)}
        attachment={attachment}
        setAttachment={setAttachment}
      />

      {showBookClientModal && (
        <BookClientModal
          event={showBookClientModal}
          onClose={() => setShowBookClientModal(null)}
          onBooked={() => loadEvents(showArchived)}
        />
      )}
    </div>
  );
};

// ============================================================
//  📝 Форма создания/редактирования мероприятия
// ============================================================

const EventForm: React.FC<{
  event: Event | null;
  onSave: (data: any) => void;
  onCancel: () => void;
}> = ({ event, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: event?.title || '',
    description: event?.description || '',
    datetime: event?.datetime ? toLocalDatetimeString(event.datetime) : '',
    price: event?.price?.toString() || '',
    maxParticipants: event?.max_participants?.toString() || '',
    location: event?.location || '',
    isActive: event?.is_active !== false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...form,
      price: form.price ? parseFloat(form.price) : null,
      maxParticipants: parseInt(form.maxParticipants),
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{
      border: '1px solid #ddd',
      borderRadius: 8,
      padding: 16,
      marginBottom: 16,
      backgroundColor: '#f9f9f9'
    }}>
      <h3 style={{ margin: '0 0 12px 0' }}>{event ? '✏️ Редактировать' : '➕ Новое мероприятие'}</h3>

      <div style={{ marginBottom: 8 }}>
        <label style={{ display: 'block', fontWeight: 'bold' }}>Название *</label>
        <input
          type="text"
          value={form.title}
          onChange={e => setForm({ ...form, title: e.target.value })}
          required
          style={{ width: '100%', padding: 6 }}
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <label style={{ display: 'block', fontWeight: 'bold' }}>Описание</label>
        <textarea
          value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
          style={{ width: '100%', padding: 6, minHeight: 120, resize: 'vertical' }}
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <label style={{ display: 'block', fontWeight: 'bold' }}>Дата и время *</label>
        <input
          type="datetime-local"
          value={form.datetime}
          onChange={e => setForm({ ...form, datetime: e.target.value })}
          required
          style={{ width: '100%', padding: 6 }}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', fontWeight: 'bold' }}>Цена (₽)</label>
          <input
            type="number"
            value={form.price}
            onChange={e => setForm({ ...form, price: e.target.value })}
            style={{ width: '100%', padding: 6 }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', fontWeight: 'bold' }}>Макс. участников *</label>
          <input
            type="number"
            value={form.maxParticipants}
            onChange={e => setForm({ ...form, maxParticipants: e.target.value })}
            required
            style={{ width: '100%', padding: 6 }}
          />
        </div>
      </div>

      <div style={{ marginBottom: 8 }}>
        <label style={{ display: 'block', fontWeight: 'bold' }}>Место</label>
        <input
          type="text"
          value={form.location}
          onChange={e => setForm({ ...form, location: e.target.value })}
          style={{ width: '100%', padding: 6 }}
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <label>
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={e => setForm({ ...form, isActive: e.target.checked })}
          />
          Активно
        </label>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" style={{ padding: '8px 16px', cursor: 'pointer' }}>
          💾 Сохранить
        </button>
        <button type="button" onClick={onCancel} style={{ padding: '8px 16px', cursor: 'pointer' }}>
          ❌ Отмена
        </button>
      </div>
    </form>
  );
};

// ============================================================
//  📋 Модальное окно со списком записей
// ============================================================

const BookingsModal: React.FC<{
  eventTitle: string;
  bookings: Booking[];
  onClose: () => void;
  onDeleteBooking: (bookingId: number, userName: string) => void;
  onSendMessageToUser: (chatId: string, userName: string, fileAttachment?: { type: string; token: string } | null) => void;
  onSendMessageToAll: () => void;
  onViewUser: (userId: number) => void;
  attachment: { type: string; token: string } | null;
  setAttachment: (attachment: { type: string; token: string } | null) => void;
}> = ({ eventTitle, bookings, onClose, onDeleteBooking, onSendMessageToUser, onSendMessageToAll, onViewUser, attachment, setAttachment }) => {
  if (bookings.length === 0) {
    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}>
        <div style={{
          backgroundColor: '#fff',
          padding: 24,
          borderRadius: 12,
          maxWidth: 500,
          width: '100%',
        }}>
          <h3>📋 {eventTitle}</h3>
          <p>Нет записей на это мероприятие</p>
          <button onClick={onClose} style={{ marginTop: 16, padding: '8px 16px', cursor: 'pointer' }}>
            Закрыть
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#fff',
        padding: 24,
        borderRadius: 12,
        maxWidth: 550,
        width: '100%',
        maxHeight: '80%',
        overflow: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>📋 {eventTitle}</h3>
          <button
            onClick={onSendMessageToAll}
            style={{
              padding: '6px 12px',
              cursor: 'pointer',
              backgroundColor: '#007AFF',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              fontSize: 13,
            }}
          >
            ✉️ Всем
          </button>
        </div>
        <FileAttachment onAttachmentChange={setAttachment} />

        <p style={{ marginBottom: 12 }}>Записано: {bookings.length} человек</p>

        {bookings.map(booking => {
          const isGuest = booking.is_guest === true;
          const guestName = booking.guest_name;
          const userName = booking.user_name || 'Неизвестный';

          return (
            <div key={booking.booking_id} style={{
              borderBottom: '1px solid #eee',
              padding: '8px 0',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div>
                    {isGuest ? (
                      <>
                        👥 <strong>{guestName || 'Гость'}</strong> <span style={{ fontSize: 12, color: '#888' }}>(гость)</span>
                        <span style={{ fontSize: 13, color: '#666', marginLeft: 8 }}>
                          — записал{' '}
                          <span
                            onClick={() => onViewUser(booking.user_id)}
                            style={{ cursor: 'pointer', color: '#007AFF', textDecoration: 'underline' }}
                            title="Открыть карточку клиента"
                          >
                            {userName}
                          </span>
                        </span>
                        {booking.admin_note && (
                          <span style={{
                            fontSize: 11,
                            color: '#007AFF',
                            marginLeft: 6,
                            fontStyle: 'italic',
                          }}>
                            ({booking.admin_note})
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <strong
                          onClick={() => onViewUser(booking.user_id)}
                          style={{ cursor: 'pointer', color: '#007AFF', textDecoration: 'underline' }}
                          title="Открыть карточку клиента"
                        >
                          {userName}
                        </strong>
                        {booking.admin_note && (
                          <span style={{
                            fontSize: 11,
                            color: '#007AFF',
                            marginLeft: 6,
                            fontStyle: 'italic',
                          }}>
                            ({booking.admin_note})
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    📅 {new Date(booking.booked_at).toLocaleString()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {booking.chat_id && (
                    <button
                      onClick={() => {
                        if (booking.chat_id) {
                          onSendMessageToUser(booking.chat_id, booking.user_name || 'Пользователь', attachment);
                        }
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#007AFF',
                        fontSize: 18,
                        cursor: 'pointer',
                        padding: '4px 6px',
                        borderRadius: 4,
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e3f2fd';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                      title="Написать сообщение"
                    >
                      ✉️
                    </button>
                  )}
                  <button
                    onClick={() => onDeleteBooking(booking.booking_id, booking.user_name || 'Пользователя')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ff4444',
                      fontSize: 18,
                      cursor: 'pointer',
                      padding: '4px 6px',
                      borderRadius: 4,
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#fee';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                    title="Удалить запись"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        <button onClick={onClose} style={{ marginTop: 16, padding: '8px 16px', cursor: 'pointer' }}>
          Закрыть
        </button>
      </div>
    </div>
  );
};

// ============================================================
//  👥 Модальное окно со списком пользователей
// ============================================================

const UsersModal: React.FC<{
  isOpen: boolean;
  users: User[];
  onClose: () => void;
  onViewUser: (userId: number) => void;
  onUpdateNote: (userId: number, note: string) => Promise<void>;
}> = ({ isOpen, users, onClose, onViewUser, onUpdateNote }) => {
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');

  if (!isOpen) return null;

  const handleStartEdit = (user: User) => {
    setEditingUserId(user.id);
    setEditValue(user.admin_note || '');
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditValue('');
  };

  const handleSaveEdit = async () => {
    if (editingUserId !== null) {
      await onUpdateNote(editingUserId, editValue.trim());
      setEditingUserId(null);
      setEditValue('');
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#fff',
        padding: 24,
        borderRadius: 12,
        maxWidth: 800,
        width: '100%',
        maxHeight: '80%',
        overflow: 'auto',
      }}>
        <h3 style={{ margin: '0 0 16px 0' }}>👥 Пользователи</h3>
        {users.length === 0 ? (
          <p>Нет пользователей</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd' }}>
                <th style={{ textAlign: 'left', padding: '8px 4px' }}>Имя</th>
                <th style={{ textAlign: 'left', padding: '8px 4px' }}>Заметка</th>
                <th style={{ textAlign: 'left', padding: '8px 4px' }}>Роль</th>
                <th style={{ textAlign: 'left', padding: '8px 4px' }}>Дата подключения</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const isEditing = editingUserId === user.id;

                return (
                  <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px 4px', verticalAlign: 'top' }}>
                      <div
                        style={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          color: '#007AFF',
                          textDecoration: 'underline',
                        }}
                        onClick={() => onViewUser(user.id)}
                        title="Открыть карточку"
                      >
                        {user.full_name}
                      </div>
                      {user.username && (
                        <div style={{ fontSize: 11, color: '#007AFF' }}>@{user.username}</div>
                      )}
                    </td>
                    <td style={{ padding: '8px 4px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            maxLength={200}
                            placeholder="Например: коллега Ольги"
                            autoFocus
                            style={{
                              flex: 1,
                              padding: '4px 6px',
                              fontSize: 13,
                              border: '1px solid #007AFF',
                              borderRadius: 4,
                              minWidth: 120,
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEdit();
                              if (e.key === 'Escape') handleCancelEdit();
                            }}
                          />
                          <button
                            onClick={handleSaveEdit}
                            style={{
                              padding: '4px 8px',
                              background: '#007AFF',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                            }}
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            style={{
                              padding: '4px 8px',
                              background: '#f0f0f0',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: 'pointer',
                          }}
                          onClick={() => handleStartEdit(user)}
                          title="Нажмите, чтобы изменить заметку"
                        >
                          <span style={{
                            fontSize: 13,
                            color: user.admin_note ? '#333' : '#bbb',
                            fontStyle: user.admin_note ? 'normal' : 'italic',
                          }}>
                            {user.admin_note || 'нет заметки'}
                          </span>
                          <span style={{ fontSize: 11, color: '#007AFF' }}>✏️</span>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '8px 4px', verticalAlign: 'top' }}>{user.role}</td>
                    <td style={{ padding: '8px 4px', verticalAlign: 'top' }}>
                      {new Date(user.created_at).toLocaleString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <button onClick={onClose} style={{ marginTop: 16, padding: '8px 16px', cursor: 'pointer' }}>
          Закрыть
        </button>
      </div>
    </div>
  );
};

// ============================================================
//  👤 Модальное окно со списком гостей
// ============================================================

const GuestsModal: React.FC<{
  isOpen: boolean;
  guests: Guest[];
  onClose: () => void;
}> = ({ isOpen, guests, onClose }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#fff',
        padding: 24,
        borderRadius: 12,
        maxWidth: 700,
        width: '100%',
        maxHeight: '80%',
        overflow: 'auto',
      }}>
        <h3 style={{ margin: '0 0 16px 0' }}>👤 Гости</h3>
        {guests.length === 0 ? (
          <p>Нет гостей</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd' }}>
                <th style={{ textAlign: 'left', padding: '8px 4px' }}>Имя</th>
                <th style={{ textAlign: 'left', padding: '8px 4px' }}>Записей</th>
                <th style={{ textAlign: 'left', padding: '8px 4px' }}>Владелец</th>
                <th style={{ textAlign: 'left', padding: '8px 4px' }}>Дата создания</th>
              </tr>
            </thead>
            <tbody>
              {guests.map(guest => (
                <tr key={guest.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px 4px' }}><strong>{guest.full_name}</strong></td>
                  <td style={{ padding: '8px 4px' }}>{guest.bookings_count}</td>
                  <td style={{ padding: '8px 4px' }}>{guest.user_name || '—'}</td>
                  <td style={{ padding: '8px 4px' }}>
                    {new Date(guest.created_at).toLocaleString('ru-RU', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <button onClick={onClose} style={{ marginTop: 16, padding: '8px 16px', cursor: 'pointer' }}>
          Закрыть
        </button>
      </div>
    </div>
  );
};

// ============================================================
//  ⏳ Модальное окно со списком листа ожидания
// ============================================================

const WaitlistModal: React.FC<{
  eventTitle: string;
  entries: WaitlistEntry[];
  onClose: () => void;
  onDeleteEntry: (waitlistId: number, userName: string) => void;
  onSendMessageToUser: (chatId: string, userName: string, fileAttachment?: { type: string; token: string } | null) => void;
  onViewUser: (userId: number) => void;
  attachment: { type: string; token: string } | null;
  setAttachment: (attachment: { type: string; token: string } | null) => void;
}> = ({
  eventTitle,
  entries,
  onClose,
  onDeleteEntry,
  onSendMessageToUser,
  onViewUser,
  attachment,
  setAttachment
}) => {
  if (entries.length === 0) {
    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}>
        <div style={{
          backgroundColor: '#fff',
          padding: 24,
          borderRadius: 12,
          maxWidth: 500,
          width: '100%',
        }}>
          <h3>⏳ {eventTitle}</h3>
          <p>Лист ожидания пуст</p>
          <button onClick={onClose} style={{ marginTop: 16, padding: '8px 16px', cursor: 'pointer' }}>
            Закрыть
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#fff',
        padding: 24,
        borderRadius: 12,
        maxWidth: 600,
        width: '100%',
        maxHeight: '80%',
        overflow: 'auto',
      }}>
        <h3 style={{ margin: '0 0 12px 0' }}>⏳ Лист ожидания: {eventTitle}</h3>

        <FileAttachment onAttachmentChange={setAttachment} />

        <p style={{ marginBottom: 12 }}>Всего в очереди: {entries.length} человек</p>

        {entries.map((entry, index) => {
          const isGuest = entry.is_guest === true;

          return (
            <div key={entry.waitlist_id} style={{
              borderBottom: '1px solid #eee',
              padding: '10px 0',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 'bold', color: '#888' }}>#{index + 1}</span>

                    {isGuest ? (
                      <>
                        <span style={{ fontSize: 16 }}>👥</span>
                        <strong>{entry.guest_name || 'Без имени'}</strong>
                        <span style={{
                          fontSize: 11,
                          padding: '2px 6px',
                          borderRadius: 8,
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                        }}>
                          (гость)
                        </span>
                        <span style={{ fontSize: 12, color: '#666' }}>
                          — записал{' '}
                          <strong
                            onClick={() => onViewUser(entry.user_id)}
                            style={{ cursor: 'pointer', color: '#007AFF', textDecoration: 'underline' }}
                            title="Открыть карточку клиента"
                          >
                            {entry.user_name}
                          </strong>
                        </span>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: 16 }}>👤</span>
                        <strong
                          onClick={() => onViewUser(entry.user_id)}
                          style={{ cursor: 'pointer', color: '#007AFF', textDecoration: 'underline' }}
                          title="Открыть карточку клиента"
                        >
                          {entry.user_name}
                        </strong>
                        {entry.username && (
                          <span style={{ fontSize: 12, color: '#999' }}>(@{entry.username})</span>
                        )}
                      </>
                    )}

                    {entry.admin_note && (
                      <span style={{
                        fontSize: 11,
                        color: '#007AFF',
                        fontStyle: 'italic',
                      }}>
                        ({entry.admin_note})
                      </span>
                    )}
                  </div>

                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    📅 {new Date(entry.created_at).toLocaleString('ru-RU', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 10px',
                    borderRadius: 12,
                    fontSize: 11,
                    backgroundColor: entry.status === 'pending' ? '#ff9800' : '#4CAF50',
                    color: '#fff',
                    whiteSpace: 'nowrap',
                  }}>
                    {entry.status === 'pending' ? '⏳ Ожидает' : '📨 Уведомлён'}
                  </span>

                  {entry.chat_id && (
                    <button
                      onClick={() => {
                        onSendMessageToUser(
                          entry.chat_id!,
                          entry.user_name || 'Пользователь',
                          attachment
                        );
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#007AFF',
                        fontSize: 18,
                        cursor: 'pointer',
                        padding: '4px 6px',
                        borderRadius: 4,
                      }}
                      title="Написать сообщение"
                    >
                      ✉️
                    </button>
                  )}

                  <button
                    onClick={() => onDeleteEntry(entry.waitlist_id, entry.user_name || 'Пользователя')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ff4444',
                      fontSize: 18,
                      cursor: 'pointer',
                      padding: '4px 6px',
                      borderRadius: 4,
                    }}
                    title="Удалить из листа ожидания"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        <button onClick={onClose} style={{ marginTop: 16, padding: '8px 16px', cursor: 'pointer' }}>
          Закрыть
        </button>
      </div>
    </div>
  );
};

// ============================================================
//  ✉️ Модальное окно рассылки
// ============================================================

const BroadcastModal: React.FC<{
  isOpen: boolean;
  users: User[];
  onClose: () => void;
  attachment: { type: string; token: string } | null;
  setAttachment: (attachment: { type: string; token: string } | null) => void;
}> = ({ isOpen, users, onClose, attachment, setAttachment }) => {
  const [toAll, setToAll] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!message.trim()) {
      alert('Введите сообщение');
      return;
    }

    if (!toAll && selectedUserIds.length === 0) {
      alert('Выберите хотя бы одного пользователя или отметьте "Всем"');
      return;
    }

    setSending(true);
    try {
      const payload: any = { message: message.trim() };
      if (toAll) {
        payload.recipient = 'all';
      } else {
        payload.recipient = selectedUserIds;
      }
      if (attachment) {
        payload.attachments = [{ type: attachment.type, token: attachment.token }];
      }

      const response = await api.post('/admin/broadcast', payload);
      notify.success(`✅ Сообщение отправлено: ${response.data.sent} получателей, ошибок: ${response.data.failed}`);
      onClose();
    } catch (error) {
      console.error('❌ Ошибка отправки рассылки:', error);
      notify.error('Не удалось отправить сообщение');
    } finally {
      setSending(false);
    }
  };

  const toggleUser = (userId: number) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllUsers = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map(u => u.id));
    }
  };

  const handleToAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setToAll(checked);
    if (checked) {
      setSelectedUserIds([]);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#fff',
        padding: 24,
        borderRadius: 12,
        maxWidth: 550,
        width: '100%',
        maxHeight: '90%',
        overflow: 'auto',
      }}>
        <h3 style={{ margin: '0 0 16px 0' }}>✉️ Отправить сообщение</h3>

        <FileAttachment onAttachmentChange={setAttachment} />

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 4 }}>
            <input
              type="checkbox"
              checked={toAll}
              onChange={handleToAllChange}
            />
            Всем пользователям
          </label>
        </div>

        {!toAll && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ fontWeight: 'bold' }}>Выберите получателей</label>
              <button
                onClick={toggleAllUsers}
                style={{ fontSize: 12, background: 'none', border: 'none', color: '#007AFF', cursor: 'pointer' }}
              >
                {selectedUserIds.length === users.length ? 'Снять всех' : 'Выбрать всех'}
              </button>
            </div>
            <div style={{
              border: '1px solid #ddd',
              borderRadius: 4,
              maxHeight: 180,
              overflow: 'auto',
              padding: '4px 0',
            }}>
              {users.map(user => (
                <label
                  key={user.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedUserIds.includes(user.id)}
                    onChange={() => toggleUser(user.id)}
                    style={{ marginRight: 8 }}
                  />
                  <span>{user.full_name}</span>
                  {user.admin_note && (
                    <span style={{ fontSize: 11, color: '#007AFF', marginLeft: 6, fontStyle: 'italic' }}>
                      ({user.admin_note})
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: '#999', marginLeft: 8 }}>({user.role})</span>
                </label>
              ))}
            </div>
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
              Выбрано: {selectedUserIds.length} пользователей
            </div>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 4 }}>Сообщение</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            style={{ width: '100%', padding: 6, minHeight: 80, borderRadius: 4, border: '1px solid #ddd' }}
            placeholder="Введите текст сообщения..."
          />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', cursor: 'pointer', borderRadius: 4, border: '1px solid #ddd', background: '#fff' }}
            disabled={sending}
          >
            Отмена
          </button>
          <button
            onClick={handleSend}
            style={{
              padding: '8px 16px',
              cursor: sending ? 'not-allowed' : 'pointer',
              backgroundColor: '#007AFF',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              opacity: sending ? 0.6 : 1,
            }}
            disabled={sending}
          >
            {sending ? 'Отправка...' : '📨 Отправить'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
//  👤 КАРТОЧКА ПОЛЬЗОВАТЕЛЯ (с управлением ролью)
// ============================================================

const UserCardModal: React.FC<{
  userId: number;
  currentUserRole: string | null;
  onClose: () => void;
  onNoteUpdate: (userId: number, note: string) => void;
  onRoleUpdate: (userId: number, newRole: string) => Promise<void>;
  onSendMessage: (chatId: string, userName: string) => void;
}> = ({ userId, currentUserRole, onClose, onNoteUpdate, onRoleUpdate, onSendMessage }) => {
  const [user, setUser] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noteValue, setNoteValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  // Состояния для смены роли
  const [newRole, setNewRole] = useState<string>('');
  const [savingRole, setSavingRole] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/admin/users/${userId}`);
        setUser(response.data);
        setNoteValue(response.data.admin_note || '');
        setNewRole(response.data.role || 'client');
      } catch (err: any) {
        console.error('❌ Ошибка загрузки пользователя:', err);
        setError('Не удалось загрузить данные пользователя');
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [userId]);

  const handleSaveNote = async () => {
    if (!user) return;

    setSaving(true);
    setSavedSuccess(false);
    try {
      await api.put(`/admin/users/${user.id}/note`, { note: noteValue.trim() });
      setUser({ ...user, admin_note: noteValue.trim() || undefined });
      onNoteUpdate(user.id, noteValue.trim());
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 2000);
    } catch (err) {
      console.error('❌ Ошибка сохранения заметки:', err);
      alert('Не удалось сохранить заметку');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeRole = async () => {
    if (!user || newRole === user.role) return;

    const roleLabel: Record<string, string> = {
      client: 'Клиент',
      admin: 'Администратор',
      tech: 'Технический администратор',
    };

    if (!window.confirm(
      `Изменить роль пользователя ${user.full_name} с «${roleLabel[user.role] || user.role}» на «${roleLabel[newRole] || newRole}»?`
    )) return;

    setSavingRole(true);
    try {
      await onRoleUpdate(user.id, newRole);
      setUser({ ...user, role: newRole });
    } catch (err) {
      // Откатываем в случае ошибки
      setNewRole(user.role);
    } finally {
      setSavingRole(false);
    }
  };

  const hasNoteChanges = user && (noteValue.trim() !== (user.admin_note || ''));

  // Может ли текущий пользователь менять роли
  const canManageRoles = currentUserRole === 'owner' || currentUserRole === 'tech';
  // Можно ли менять роль этого пользователя
  const canChangeThisRole = user && user.role !== 'owner';
  // Tech не может трогать других tech
  const isTechRestricted = currentUserRole === 'tech' && user?.role === 'tech';

  // Опции для селекта роли
  const roleOptions = [
    { value: 'client', label: 'Клиент' },
    { value: 'admin', label: 'Администратор' },
    ...(currentUserRole === 'owner' ? [{ value: 'tech', label: 'Технический администратор' }] : []),
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1100,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          padding: 24,
          borderRadius: 12,
          maxWidth: 600,
          width: '100%',
          maxHeight: '90%',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>👤 Карточка клиента</h3>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {user && user.chat_id && (
              <button
                onClick={() => onSendMessage(user.chat_id!, user.full_name || 'Пользователь')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007AFF',
                  fontSize: 20,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: 4,
                }}
                title="Написать сообщение"
              >
                ✉️
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 22,
                cursor: 'pointer',
                color: '#999',
                padding: '0 6px',
              }}
              title="Закрыть"
            >
              ✕
            </button>
          </div>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', color: '#666' }}>Загрузка...</p>
        ) : error ? (
          <p style={{ color: '#ff4444' }}>{error}</p>
        ) : user ? (
          <>
            <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f9f9f9', borderRadius: 8 }}>
              <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 4 }}>
                {user.full_name}
              </div>
              {user.username && (
                <div style={{ fontSize: 13, color: '#007AFF', marginBottom: 4 }}>
                  @{user.username}
                </div>
              )}
              <div style={{ fontSize: 12, color: '#666' }}>
                ID в MAX: {user.messenger_id}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                Роль: <span style={{ fontWeight: 'bold' }}>{user.role}</span>
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                Дата регистрации: {new Date(user.created_at).toLocaleString('ru-RU', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{
                flex: 1, padding: 12, backgroundColor: '#e8f5e9',
                borderRadius: 8, textAlign: 'center',
              }}>
                <div style={{ fontSize: 22, fontWeight: 'bold', color: '#4CAF50' }}>
                  {user.bookings_count}
                </div>
                <div style={{ fontSize: 11, color: '#666' }}>Активных записей</div>
              </div>
              <div style={{
                flex: 1, padding: 12, backgroundColor: '#e3f2fd',
                borderRadius: 8, textAlign: 'center',
              }}>
                <div style={{ fontSize: 22, fontWeight: 'bold', color: '#1976d2' }}>
                  {user.guests_count}
                </div>
                <div style={{ fontSize: 11, color: '#666' }}>Гостей</div>
              </div>
            </div>

            {/* ============================================================
                👑 СМЕНА РОЛИ
            ============================================================ */}
            {canManageRoles && canChangeThisRole && (
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff8e1', borderRadius: 8, border: '1px solid #ffe082' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>
                  👑 Роль пользователя
                </label>

                {isTechRestricted ? (
                  <p style={{ fontSize: 12, color: '#999', margin: 0, fontStyle: 'italic' }}>
                    Tech не может изменять роль другого tech. Это может сделать только владелец.
                  </p>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <select
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value)}
                        disabled={savingRole}
                        style={{
                          flex: 1,
                          padding: 8,
                          fontSize: 14,
                          border: '1px solid #ddd',
                          borderRadius: 6,
                          backgroundColor: '#fff',
                        }}
                      >
                        {roleOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <button
                        onClick={handleChangeRole}
                        disabled={newRole === user.role || savingRole}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: newRole !== user.role ? '#007AFF' : '#ccc',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          cursor: newRole !== user.role ? 'pointer' : 'default',
                          fontSize: 13,
                          opacity: savingRole ? 0.6 : 1,
                        }}
                      >
                        {savingRole ? 'Сохранение...' : 'Изменить'}
                      </button>
                    </div>
                    <p style={{ fontSize: 11, color: '#999', marginTop: 6, marginBottom: 0 }}>
                      {currentUserRole === 'tech'
                        ? 'Tech может назначать только администраторов. Назначать tech может только владелец.'
                        : 'Владелец может назначать администраторов и технических администраторов.'}
                    </p>
                  </>
                )}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>
                📝 Заметка администратора
              </label>
              <textarea
                value={noteValue}
                onChange={(e) => setNoteValue(e.target.value)}
                maxLength={200}
                placeholder="Например: коллега Ольги, муж Анны..."
                rows={2}
                style={{
                  width: '100%',
                  padding: 8,
                  fontSize: 14,
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                <span style={{ fontSize: 11, color: '#999' }}>
                  {noteValue.length}/200 символов
                </span>
                <button
                  onClick={handleSaveNote}
                  disabled={!hasNoteChanges || saving}
                  style={{
                    padding: '6px 16px',
                    backgroundColor: hasNoteChanges ? '#007AFF' : '#ccc',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: hasNoteChanges ? 'pointer' : 'default',
                    fontSize: 13,
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  {saving ? 'Сохранение...' : savedSuccess ? '✅ Сохранено' : '💾 Сохранить'}
                </button>
              </div>
            </div>

            {user.active_bookings.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 8 }}>
                  📌 Активные записи
                </label>
                {user.active_bookings.map(b => (
                  <div
                    key={b.booking_id}
                    style={{
                      padding: 8,
                      borderBottom: '1px solid #eee',
                      fontSize: 13,
                    }}
                  >
                    <div style={{ fontWeight: 'bold' }}>
                      {b.is_guest ? `👥 Гость: ${b.guest_name || 'Без имени'}` : b.event_title}
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      📅 {new Date(b.event_datetime).toLocaleString('ru-RU', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </div>
                    {b.status === 'pending_payment' && (
                      <span style={{
                        display: 'inline-block',
                        fontSize: 10,
                        padding: '2px 6px',
                        borderRadius: 8,
                        backgroundColor: '#ff9800',
                        color: '#fff',
                        marginTop: 2,
                      }}>
                        ⏳ Ожидает оплаты
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  cursor: 'pointer',
                  borderRadius: 6,
                  border: '1px solid #ddd',
                  background: '#fff',
                }}
              >
                Закрыть
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

// ============================================================
//  📝 ЗАПИСЬ КЛИЕНТА АДМИНИСТРАТОРОМ
// ============================================================

interface SearchUser {
  id: number;
  full_name: string;
  username?: string;
  messenger_id: string;
  chat_id?: string;
  admin_note?: string;
}

const BookClientModal: React.FC<{
  event: Event;
  onClose: () => void;
  onBooked: () => void;
}> = ({ event, onClose, onBooked }) => {
  const [allUsers, setAllUsers] = useState<SearchUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [filter, setFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState<SearchUser | null>(null);

  const [isGuest, setIsGuest] = useState(false);
  const [guestName, setGuestName] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoadingUsers(true);
      try {
        const res = await api.get('/admin/users');
        setAllUsers(res.data);
      } catch (err) {
        console.error('Ошибка загрузки пользователей:', err);
        setError('Не удалось загрузить список клиентов');
      } finally {
        setLoadingUsers(false);
      }
    };
    load();
  }, []);

  const filteredUsers = allUsers.filter(u => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      u.full_name.toLowerCase().includes(q) ||
      (u.username || '').toLowerCase().includes(q) ||
      u.messenger_id.toLowerCase().includes(q) ||
      (u.admin_note || '').toLowerCase().includes(q)
    );
  });

  const handleBook = async () => {
    if (!selectedUser) {
      setError('Выберите клиента');
      return;
    }

    if (isGuest && !guestName.trim()) {
      setError('Введите имя гостя');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const payload: any = {
        eventId: event.id,
        userId: selectedUser.id,
      };

      if (isGuest) {
        payload.guestName = guestName.trim();
      }

      const res = await api.post('/admin/book', payload);
      notify.success(res.data.message || 'Клиент записан');
      onBooked();
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Не удалось записать клиента';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1100,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          padding: 24,
          borderRadius: 12,
          maxWidth: 560,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>📝 Запись клиента</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#999' }}
          >
            ✕
          </button>
        </div>

        <div style={{
          padding: 12,
          backgroundColor: '#f5f5f5',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{event.title}</div>
          <div style={{ color: '#666' }}>
            📅 {new Date(event.datetime).toLocaleString('ru-RU', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}
          </div>
          <div style={{ color: '#666' }}>
            🆓 Свободных мест: <b>{event.available_slots}</b> из {event.max_participants}
          </div>
        </div>

        {!selectedUser ? (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>
              🔍 Выбор клиента
            </label>

            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Начните вводить имя для фильтрации"
              autoFocus
              style={{
                width: '100%',
                padding: 10,
                fontSize: 14,
                border: '1px solid #ddd',
                borderRadius: 6,
                marginBottom: 8,
              }}
            />

            {loadingUsers ? (
              <p style={{ fontSize: 13, color: '#999', textAlign: 'center', padding: 12 }}>
                Загрузка списка клиентов...
              </p>
            ) : allUsers.length === 0 ? (
              <p style={{ fontSize: 13, color: '#999', textAlign: 'center', padding: 12 }}>
                Пока нет клиентов
              </p>
            ) : filteredUsers.length === 0 ? (
              <p style={{ fontSize: 13, color: '#999', textAlign: 'center', padding: 12 }}>
                Ничего не найдено по запросу «{filter}»
              </p>
            ) : (
              <div style={{
                border: '1px solid #eee',
                borderRadius: 6,
                maxHeight: 320,
                overflow: 'auto',
              }}>
                {filteredUsers.map(u => (
                  <div
                    key={u.id}
                    onClick={() => setSelectedUser(u)}
                    style={{
                      padding: 10,
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f5f5'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ fontWeight: 'bold' }}>
                      {u.full_name}
                      {u.admin_note && (
                        <span style={{ fontSize: 11, color: '#007AFF', fontStyle: 'italic', marginLeft: 6 }}>
                          ({u.admin_note})
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {u.username && `@${u.username} · `}ID: {u.messenger_id}
                      {!u.chat_id && <span style={{ color: '#ff4444' }}> · нет chat_id</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loadingUsers && allUsers.length > 0 && (
              <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>
                Показано: {filteredUsers.length} из {allUsers.length}
              </div>
            )}
          </div>
        ) : (
          <div style={{
            padding: 12,
            backgroundColor: '#e3f2fd',
            borderRadius: 8,
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div>
              <div style={{ fontWeight: 'bold' }}>👤 {selectedUser.full_name}</div>
              <div style={{ fontSize: 12, color: '#666' }}>
                {selectedUser.username && `@${selectedUser.username} · `}ID: {selectedUser.messenger_id}
              </div>
            </div>
            <button
              onClick={() => setSelectedUser(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#007AFF',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              Изменить
            </button>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
            <input
              type="checkbox"
              checked={isGuest}
              onChange={(e) => setIsGuest(e.target.checked)}
            />
            Записать на гостя
          </label>

          {isGuest && (
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="Имя гостя"
              style={{
                width: '100%',
                padding: 8,
                fontSize: 14,
                border: '1px solid #ddd',
                borderRadius: 6,
                marginTop: 8,
              }}
            />
          )}
        </div>

        {error && (
          <div style={{
            padding: 10,
            backgroundColor: '#ffebee',
            color: '#c62828',
            borderRadius: 6,
            fontSize: 13,
            marginBottom: 12,
          }}>
            ❌ {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              borderRadius: 6,
              border: '1px solid #ddd',
              background: '#fff',
            }}
          >
            Отмена
          </button>
          <button
            onClick={handleBook}
            disabled={!selectedUser || submitting}
            style={{
              padding: '8px 20px',
              cursor: (!selectedUser || submitting) ? 'default' : 'pointer',
              backgroundColor: !selectedUser ? '#ccc' : '#007AFF',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontWeight: 'bold',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? 'Запись...' : '📝 Записать'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;